/**
 * useChat — Global singleton state + actions for the AI chat sidebar.
 *
 * Wraps useClaude for transport; owns the chat-specific state: persisted
 * message history, open/close, current record, navigate handoff, retry,
 * and saving a message back out as a document record.
 *
 * The state is shared module-wide; the record reads and the document save run
 * through the calling component's `client`.
 */

import { readonly, type Ref, ref } from "vue"
import { formatMonthYear } from "@auger/ui/utils/date"
import { fitSlug, slugify } from "@auger/ui/utils/string"
import { fetchDetailedRecordBySlug } from "@/common/hasura/records"
import type { ComposableDeps } from "@/composables/types"
import {
  type ChatMessage,
  mapRecordForContext,
  type NavigateAction,
  type RecordContext,
  recordToMarkdown,
  useClaude,
} from "@/composables/useClaude"
import { graphql } from "@/gql"
import { getAuger } from "@/init/auger"
import { buildPrompt } from "@/stores/prompts"

// ── Queries ────────────────────────────────────────────────────────

const fetchDocumentSchema = graphql(`
  query fetchDocumentSchema {
    schemas(where: { name: { _eq: "All Documents" } }, limit: 1) {
      id
    }
  }
`)

// ── Singleton state ────────────────────────────────────────────────
const messages: Ref<ChatMessage[]> = ref([])
const isOpen = ref(false)
const currentRecord: Ref<RecordContext | null> = ref(null)
const pendingNavigate: Ref<NavigateAction | null> = ref(null)
const savingDocument = ref(false)

const claude = useClaude()
const { isStreaming, error } = claude

// ── LocalStorage persistence ───────────────────────────────────────
const STORAGE_KEY = "auger-ai-chat"

function loadFromStorage() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      if (Array.isArray(parsed)) messages.value = parsed
    }
  } catch {
    // Ignore corrupt storage
  }
}

function saveToStorage() {
  try {
    const toSave = messages.value.filter((m) => m.content)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave))
  } catch {
    // Storage full or unavailable
  }
}

loadFromStorage()

if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", saveToStorage)
}

// ── Record context ─────────────────────────────────────────────────

function clearRecord() {
  currentRecord.value = null
}

// ── Actions ────────────────────────────────────────────────────────

function open() {
  isOpen.value = true
}
function close() {
  isOpen.value = false
}
function toggle() {
  isOpen.value = !isOpen.value
}

function clear() {
  claude.cancel()
  messages.value = []
  localStorage.removeItem(STORAGE_KEY)
}

function buildSystemPrompt(): Promise<string> {
  const context = currentRecord.value ? recordToMarkdown(currentRecord.value) : null
  const recordContext = context
    ? "\n\nHere is context about the record the user is currently viewing:\n\n" + context
    : "\n\nThe user is browsing the app but not viewing a specific record."
  return buildPrompt("ai-chat-system", { record_context: recordContext })
}

function send(userMessage: string): Promise<void> {
  if (!userMessage.trim() || isStreaming.value) return Promise.resolve()
  error.value = null

  messages.value.push({
    role: "user",
    content: userMessage.trim(),
    timestamp: Date.now(),
  })
  saveToStorage()

  const apiMessages = trimMessages(messages.value)

  const assistantMessage: ChatMessage = {
    role: "assistant",
    content: "",
    timestamp: Date.now(),
  }
  messages.value.push(assistantMessage)
  pendingNavigate.value = null

  return buildSystemPrompt()
    .then((systemPrompt) =>
      claude.stream(
        systemPrompt,
        apiMessages,
        (delta) => {
          assistantMessage.content += delta
        },
        {
          onNavigate: (action) => {
            pendingNavigate.value = action
          },
        }
      )
    )
    .catch((err: Error) => {
      // claude.stream surfaces its own failures through `error`; building the
      // prompt runs before it, so a failed prompt load is surfaced here.
      if (!error.value) error.value = err?.message || "Could not load the chat prompt"
      // Strip the empty assistant placeholder.
      if (!assistantMessage.content) {
        const idx = messages.value.indexOf(assistantMessage)
        if (idx !== -1) messages.value.splice(idx, 1)
      }
    })
    .finally(() => {
      saveToStorage()
    })
}

function retry() {
  const lastUserIdx = messages.value.findLastIndex((m) => m.role === "user")
  if (lastUserIdx === -1) return
  const lastUserMsg = messages.value[lastUserIdx]!.content
  messages.value.splice(lastUserIdx)
  return send(lastUserMsg)
}

// ── Helpers ────────────────────────────────────────────────────────

function trimMessages(msgs: ChatMessage[]): ChatMessage[] {
  const maxMessages = 10
  const maxContentLength = 500

  const apiMsgs = msgs.filter((m) => m.role === "user" || (m.role === "assistant" && m.content))
  const recent = apiMsgs.length > maxMessages ? apiMsgs.slice(-maxMessages) : apiMsgs

  return recent.map((m) => ({
    role: m.role,
    content:
      m.role === "assistant" && m.content.length > maxContentLength
        ? m.content.slice(0, maxContentLength) + "\n[earlier response truncated]"
        : m.content,
  }))
}

export function useChat({ client }: Pick<ComposableDeps, "client">) {
  function setRecord(slug: string, type: string): Promise<void> {
    return client
      .query({
        query: fetchDetailedRecordBySlug,
        variables: { type, slug },
        fetchPolicy: "network-only",
      })
      .then(({ data }) => {
        const rec = data?.records?.[0]
        if (!rec) throw new Error(`Record not found: ${type}/${slug}`)
        currentRecord.value = rec ? mapRecordForContext(rec) : null
      })
      .catch((err: Error) => {
        console.error("Failed to load record for AI context:", err)
        currentRecord.value = null
      })
  }

  /**
   * Persist an assistant message as a new `document` record, optionally linked
   * to the currently-viewed record. Resolves with the created doc on success
   * so the caller can navigate to it.
   */
  function saveMessageAsDocument(content: string): Promise<{ type: string; slug: string }> {
    if (savingDocument.value) return Promise.reject(new Error("Save already in progress"))
    savingDocument.value = true

    const now = new Date()
    const monthYear = formatMonthYear(now)
    const recordName = currentRecord.value?.name || "Document"
    const docName = `${recordName} — ${monthYear}`
    const slugSuffix = now.getTime().toString(36)

    const description = currentRecord.value
      ? `AI-generated document for ${currentRecord.value.name}`
      : "AI-generated document"

    // Captured once so a currentRecord change mid-save (e.g. the user navigates
    // away) can't affect which record this save links to.
    const linkTarget = currentRecord.value

    // The document's related_to link is written after the create, and core
    // resolves that field from the document type's schema. Checking the schema
    // exists first makes a missing one fail before any write, rather than
    // leaving an orphaned, unlinked document behind.
    const schemaCheck = linkTarget
      ? client
          .query({ query: fetchDocumentSchema, fetchPolicy: "cache-first" })
          .then(({ data }) => {
            if (!data.schemas[0]?.id) throw new Error("Document schema not found")
          })
      : Promise.resolve()

    return schemaCheck
      .then(() =>
        getAuger(client)
          .records.create({
            type: "document",
            name: docName,
            slug: fitSlug(slugify(docName), slugSuffix),
            description,
            visibility: "private",
            data: { content },
          })
          .then((doc) => {
            if (!linkTarget) return doc
            // The record_links row IS the field value — link fields live in
            // record_links (keyed by schema_key), not in record.data.
            return getAuger(client)
              .records.update(doc.id, { related_to: { add: [linkTarget.id] } }, { read: false })
              .then(() => doc)
          })
      )
      .then((doc) => ({ type: doc.type, slug: doc.slug }))
      .finally(() => {
        savingDocument.value = false
      })
  }

  return {
    messages,
    isOpen,
    isStreaming,
    error,
    currentRecord: readonly(currentRecord),
    pendingNavigate,
    savingDocument: readonly(savingDocument),
    send,
    clear,
    open,
    close,
    toggle,
    retry,
    cancelStream: claude.cancel,
    setRecord,
    clearRecord,
    saveMessageAsDocument,
  }
}
